import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Community, type CommunityTopRatedItem } from '@/lib/api/v1';
import { Badge, Card, EmptyState, Icon, Skeleton } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import { formatDate } from '@/lib/format';

/**
 * /populaer — community top-rated, derived from non-private tasting logs
 * across every household. Authors are stripped — only aggregate avg +
 * count + a representative snapshot are shown.
 *
 * Min-count = 2 means a single rating doesn't dominate the list. We bump
 * to 1 in dev/early use when there's barely any data, but the live
 * default is 2.
 */
export function PopularPage() {
  const topQ = useQuery({
    queryKey: ['community.top-rated'],
    queryFn: () => Community.topRated({ limit: 30, minCount: 1 }),
    staleTime: 5 * 60_000,
  });

  const items = topQ.data ?? [];

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Populært</h1>
        <p className="text-sm text-mist">
          De best vurderte flaskene i Alkisen-fellesskapet — basert på smakinger fra alle
          husholdninger. Anonymisert og aggregert.
        </p>
      </header>

      {topQ.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="grid gap-3 p-3">
              <Skeleton height={152} rounded="md" />
              <div className="grid gap-2">
                <Skeleton height={18} width="80%" />
                <Skeleton height={12} width="60%" />
              </div>
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="star"
          title="Ingen vurderinger enda"
          description="Etter hvert som folk sjekker inn flasker dukker det opp en topp-liste her."
        />
      ) : (
        <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item, idx) => (
            <li key={item.productId} className="min-w-0">
              <PopularItem item={item} rank={idx + 1} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function PopularItem({ item, rank }: { item: CommunityTopRatedItem; rank: number }) {
  const meta = [item.snapshotRegion, item.snapshotCountry, item.snapshotVintage]
    .filter(Boolean)
    .join(' · ');
  const latest = formatDate(item.latestAt);
  return (
    <Link
      to={`/bottle/${encodeURIComponent(item.productId)}`}
      className="ui-card ui-card--interactive grid min-h-full gap-3 p-3"
    >
      <div className="relative grid h-36 place-items-center overflow-hidden rounded-md border border-line bg-ink-soft sm:h-40 lg:h-36 xl:h-40 2xl:h-44">
        <BottleImage
          src={item.snapshotImageUrl}
          alt={item.snapshotName}
          iconSize={48}
          mode="lightbox"
        />
        <span className="absolute left-2 top-2 rounded-sm bg-parchment px-2.5 py-1 font-serif text-lg leading-none text-ink shadow-cellar">
          #{rank}
        </span>
        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-sm bg-candle px-2 py-1 text-sm font-semibold tabular-nums text-on-primary shadow-cellar">
          <Icon name="star" size={13} /> {item.averageRating.toFixed(1)}
        </span>
      </div>
      <div className="grid gap-2 px-1 pb-1">
        <div className="grid gap-1">
          <h3 className="font-serif text-lg leading-tight text-parchment line-clamp-2">{item.snapshotName}</h3>
          <p className="min-h-[2rem] text-xs leading-relaxed text-mist line-clamp-2">
            {meta || 'Fellesskapets favoritt'}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge tone="gold">
            {item.ratingCount} {item.ratingCount === 1 ? 'vurdering' : 'vurderinger'}
          </Badge>
          <Badge tone="mist">Sist smakt {latest}</Badge>
        </div>
        <span className="pt-1 text-[10px] uppercase tracking-widest text-mist">#{item.productId}</span>
      </div>
    </Link>
  );
}
